// sessionService.js
import services from "@/composable/services";
import router from "@/router";
import { Fetcher } from "@/services/Fetcher";

// ---- Defaults ( fallback en cas d'échec de GET /api/v1/session/config ) ----
const MAX_AGE = 60 * 60 * 1000; // une heure
const TIME_ALERT = 5 * 60 * 1000; // 5 minutes

// ---- Auto-refresh silencieux du JWT ----
// Lorsque le user est ACTIF , son inactivity timer ( lastServerCallTime )
// est reset à chaque appel API mais le JWT côté serveur conserve son
// {@code exp} absolu. Sans intervention , au moment où l'{@code exp}
// est atteint , le prochain appel API renvoie 401 et le user est kické
// sans avertissement. On contre ça en POST-ant /refresh dans la dernière
// "fenêtre de refresh" : la durée avant expiration où on réémet un JWT
// frais en background , transparent pour le user.
//
// La fenêtre de refresh est SCALÉE par rapport au TTL de la session
// pour éviter deux pathologies :
//   - TTL court ( ex: 60 s ) avec une fenêtre fixe 60 s ferait fire le
//     refresh dès le login → boucle de refresh saturée ;
//   - TTL long ( ex: 1 h ) avec une fenêtre fixe 60 s reste OK mais on
//     aurait pu prévenir plus tôt si on voulait.
// La formule : 20 % du TTL , clampé entre 10 s ( plancher , garde une
// marge confortable même pour des TTL très courts ) et 60 s ( plafond ,
// inutile de demander un refresh plus de 1 min en avance ).
const REFRESH_AHEAD_FRACTION = 0.2;
const REFRESH_AHEAD_FLOOR_MS = 10 * 1000; // 10 s minimum avant exp
const REFRESH_AHEAD_CEIL_MS = 60 * 1000; // 60 s maximum avant exp
const REFRESH_MIN_INTERVAL_MS = 30 * 1000; // anti-spam : 1 refresh / 30 s max

function computeRefreshAheadMs(sessionDurationMs: number): number {
  return Math.max(
    REFRESH_AHEAD_FLOOR_MS,
    Math.min(sessionDurationMs * REFRESH_AHEAD_FRACTION, REFRESH_AHEAD_CEIL_MS),
  );
}

// ---- Bornes du seuil d'avertissement d'expiration ----
// Le seuil est désormais SCALÉ par rapport à la durée de session ( cf.
// `computeWarningThreshold` ). Pour une session 1 h , il vaut 5 min
// ( legacy ). Pour une session 5 min ( ex. test ), il tombe à 60 s.
// Sans ce calcul , quand sessionDuration <= TIME_ALERT , la popup
// "Your session will expire" s'affichait dès la 1re seconde après login
// ( bug observé runtime avec JWT_EXPIRATION=300 ).
const WARN_FRACTION = 0.2; // 20 % du TTL au max
const WARN_FLOOR_MS = 30 * 1000; // plancher 30 s
const WARN_CEIL_MS = TIME_ALERT; // plafond 5 min ( comportement legacy 1 h )

function computeWarningThreshold(sessionDurationMs: number): number {
  return Math.max(
    WARN_FLOOR_MS,
    Math.min(sessionDurationMs * WARN_FRACTION, WARN_CEIL_MS),
  );
}

/**
 * Extrait le timestamp d'expiration ( {@code exp} , en millisecondes
 * UNIX ) d'un JWT compact ( header.payload.signature ).
 *
 * <p>Le JWT n'est pas vérifié cryptographiquement ici - on lit juste le
 * payload qui est public ( base64-url ). La validation de signature
 * reste l'affaire du backend.
 *
 * @returns le timestamp d'expiration en ms , ou {@code null} si le
 *          token est absent / mal formé / sans champ {@code exp}.
 */
function decodeJwtExpMs(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // base64url -> base64
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // pad
    const padded = payload + "=".repeat((4 - payload.length % 4) % 4);
    const json = JSON.parse(atob(padded));
    if (typeof json?.exp === "number") {
      return json.exp * 1000; // exp est en SECONDES dans le JWT standard
    }
    return null;
  }
  catch {
    return null;
  }
}

export interface MinimalSessionExpirationWarning {
  showWarning: (seconds: number) => void;
  hideWarning: () => void;
}

export default {
  lastServerCallTime: Date.now() as number,

  sessionDuration: MAX_AGE as number,

  warningThreshold: TIME_ALERT as number,

  sessionCheckTimer: null as ReturnType<typeof setInterval> | null,

  expirationTimer: null as ReturnType<typeof setTimeout> | null,

  isWarningModalVisible: false as boolean,
  sessionExpirationWarning: null as MinimalSessionExpirationWarning | null,

  // Audit OA_FULL_REVIEW (8/5/26) - flag exposé par /api/v1/session/config
  // pour informer le frontend du mode actif côté backend pour le compteur
  // de lignes par référencetype ( DataRepository.buildReferenceSynthesis ).
  // Default true ; passé à false côté backend si admin force le COUNT
  // direct ( debug / benchmark , cf. OPENADOM_REFERENCEVALUE_COUNT_USE_STATS_TABLE ).
  // Consommé par ApplicationInfoView ( onglet "Gestion de l'application" )
  // pour afficher / masquer le bouton "Recompute exact" et son indicateur
  // de mode actif.
  referencevalueCountUseStatsTable: true as boolean,

  // ---- Auto-refresh silencieux ----
  // Garde anti-double-trigger : empêche de spammer /refresh quand
  // checkSessionStatus tique chaque seconde dans la fenêtre de refresh.
  isAutoRefreshing: false as boolean,
  // Dernier instant de refresh ( ms UNIX ). Sert de cooldown pour
  // bloquer un nouveau /refresh dans les REFRESH_MIN_INTERVAL_MS qui
  // suivent ( anti-thrash , important si le serveur tarde à réémettre
  // le JWT et qu'un autre tick essaie de refresh entre-temps ).
  lastAutoRefreshAt: 0 as number,

  init(sessionExpirationWarning: MinimalSessionExpirationWarning | null) {
    this.sessionExpirationWarning = sessionExpirationWarning;

    // #470 - Aligner le timer d'inactivité sur le TTL serveur, exposé
    // via GET /api/v1/session/config. Si l'appel échoue on conserve
    // silencieusement le fallback local (MAX_AGE = 1h).
    services.loginService.getSessionConfig()
      .then((config) => {
        if (config?.jwtExpirationSeconds && config.jwtExpirationSeconds > 0) {
          this.sessionDuration = config.jwtExpirationSeconds * 1000;
          // Recalculer le seuil d'avertissement APRÈS avoir connu le TTL
          // serveur ; sinon , avec un TTL court ( ex 5 min ) , le seuil
          // legacy ( 5 min aussi ) déclencherait la popup dès t=0.
          this.warningThreshold = computeWarningThreshold(this.sessionDuration);
        }
        if (typeof config?.referencevalueCountUseStatsTable === "boolean") {
          this.referencevalueCountUseStatsTable = config.referencevalueCountUseStatsTable;
        }
        // Audit OA_FULL_REVIEW (8/5/26) - reconfigure les caps du cache
        // ETag JS-side ( DataService.#filterListEtagCache ) à partir des
        // valeurs exposées par le backend ( openadom.cache.front.etag.* ).
        // Permet de tuner la mémoire cache sans rebuild du bundle.
        if (typeof config?.frontEtagCacheMaxEntries === "number"
          || typeof config?.frontEtagCacheMaxBytesMb === "number") {
          import("@/services/rest/DataService").then(({ DataService }) => {
            DataService.configureFilterListEtagCache({
              maxEntries: typeof config.frontEtagCacheMaxEntries === "number"
                ? config.frontEtagCacheMaxEntries
                : undefined,
              maxBytes: typeof config.frontEtagCacheMaxBytesMb === "number"
                ? config.frontEtagCacheMaxBytesMb * 1024 * 1024
                : undefined,
            });
          });
        }
      })
      .catch(() => {
        // Silencieux : fallback sur la valeur par défaut.
      });

    // Démarrer la vérification périodique
    this.startSessionCheck();

    // Mettre à jour le timestamp lors du chargement initial
    this.updateLastCallTime();

    // Ajouter une écoute sur le changement de visibilité de la page
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.checkSessionStatus();
      }
    });

    // Également sur le focus de la fenêtre (certains navigateurs/cas de figure)
    window.addEventListener("focus", () => {
      this.checkSessionStatus();
    });
  },

  // Démarrer la vérification périodique.
  //
  // Le tic est à 5 s ( au lieu de 1 s historique ) : suffisant pour
  // déclencher la popup d'avertissement et l'auto-refresh JWT à temps
  // ( les seuils sont en dizaines de secondes minimum ) , 5x moins de
  // wake-ups , impact batterie réduit. Le countdown 1 s de la popup
  // {@code SessionExpirationWarning.vue} a son propre setInterval et
  // n'est pas affecté.
  startSessionCheck() {
    if (!this.sessionCheckTimer) {
      this.sessionCheckTimer = setInterval(() => {
        this.checkSessionStatus();
      }, 5000);
    }
  },

  // sessionService.js
  updateLastCallTime() {
    this.lastServerCallTime = Date.now();

    // Réinitialiser le timer d'expiration sans toucher au check principal
    if (this.expirationTimer) {
      clearTimeout(this.expirationTimer);
      this.expirationTimer = null;
    }
    this.checkSessionStatus();
  },

  // Nouvelle méthode pour arrêter la vérification
  stopSessionCheck() {
    this.lastServerCallTime = Date.now();
  },

  // Vérifier le statut de la session
  checkSessionStatus() {
    if (router.currentRoute.value.path === "/login") return;
    const timeSinceLastCall = Date.now() - this.lastServerCallTime;
    const timeRemaining = this.sessionDuration - timeSinceLastCall;
    if (timeRemaining <= 0) {
      this.handleExpiredSession();
      return;
    }
    if (timeRemaining <= this.warningThreshold && !this.isWarningModalVisible) {
      // Proche de l'expiration "inactivité", afficher l'avertissement
      this.showWarningModal(Math.floor(timeRemaining / 1000));
    }
    // Auto-refresh silencieux du JWT côté serveur quand le user est
    // ACTIF mais que l'exp absolu approche. Sans ça , le user actif
    // est kické net au moment où l'exp est atteint ( prochain 401 ).
    this.maybeAutoRefresh();
  },

  /**
   * Déclenche {@code POST /refresh} en background si :
   *   - le JWT courant est lisible et a un {@code exp} ;
   *   - l'expiration approche ( reste moins de REFRESH_AHEAD_MS ) ;
   *   - aucun refresh n'est en cours et le cooldown
   *     ( REFRESH_MIN_INTERVAL_MS ) est écoulé ;
   *   - le warning modal n'est PAS affiché ( si l'utilisateur voit le
   *     prompt , c'est à lui de cliquer "Stay connected" - on ne
   *     veut pas faire disparaître la modal en arrière-plan ).
   */
  maybeAutoRefresh() {
    if (this.isWarningModalVisible) return;
    if (this.isAutoRefreshing) return;
    if (Date.now() - this.lastAutoRefreshAt < REFRESH_MIN_INTERVAL_MS) return;

    const expMs = decodeJwtExpMs(Fetcher.AUTHORIZATION?.value);
    if (expMs == null) return;
    const remaining = expMs - Date.now();
    // Si déjà expiré -> on laisse handleExpiredSession ( ou un 401
    // sur le prochain call ) gérer ; on ne refresh pas un token déjà mort.
    if (remaining <= 0) return;
    // La fenêtre de refresh est dimensionnée par rapport au TTL de la
    // session courante , pas un constant 60 s. Avec TTL=300s ça fait 60s ,
    // avec TTL=60s ça fait 12s , évitant la boucle de refresh sur TTL court.
    const refreshAheadMs = computeRefreshAheadMs(this.sessionDuration);
    if (remaining > refreshAheadMs) return;

    this.isAutoRefreshing = true;
    this.lastAutoRefreshAt = Date.now();
    services.loginService.refreshSession()
      .then(() => {
        // Le Fetcher capture le nouveau JWT via _handleResponse.
        // L'inactivity timer est aussi reset par updateLastCallTime
        // sur la réponse 200 du POST /refresh.
      })
      .catch(() => {
        // Échec ( ex. serveur down ) - on retombera sur le 401 du prochain
        // call qui déclenchera le flow de déconnexion classique.
      })
      .finally(() => {
        this.isAutoRefreshing = false;
      });
  },

  showWarningModal(remainingSeconds: number) {
    this.isWarningModalVisible = true;
    if (this.sessionExpirationWarning) {
      this.sessionExpirationWarning.showWarning(remainingSeconds);
    }
    // Configurer le timer d'expiration finale
    this.expirationTimer = setTimeout(() => {
      this.handleExpiredSession();
    }, remainingSeconds * 1000);
  },

  // Cacher la modal d'avertissement
  hideWarningModal() {
    this.isWarningModalVisible = false;
    if (this.sessionExpirationWarning) {
      this.sessionExpirationWarning.hideWarning();
    }
  },

  // Gérer l'expiration de session
  handleExpiredSession() {
    // Nettoyer les timers
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = null;
    }
    if (this.expirationTimer) {
      clearTimeout(this.expirationTimer);
      this.expirationTimer = null;
    }
    this.hideWarningModal();
    // Déclencher la déconnexion réelle si on n'est pas déjà sur la page de login
    if (router.currentRoute.value.path !== "/login") {
      services.loginService.logout();
    }
  },

  refreshSession(): Promise<boolean> {
    // Bug fix : neutraliser tout de suite le timer d'expiration final
    // posé par showWarningModal. Sans ça , si le POST /refresh prend
    // plus de temps que le {@code remainingSeconds} restant , le
    // setTimeout déclenche {@code handleExpiredSession()} en parallèle
    // de la réponse positive , l'utilisateur est kické alors qu'il a
    // cliqué "Rester connecté".
    if (this.expirationTimer) {
      clearTimeout(this.expirationTimer);
      this.expirationTimer = null;
    }
    return services.loginService
      .refreshSession()
      .then((response: unknown) => {
        if (response) {
          this.updateLastCallTime();
          this.hideWarningModal();
          this.checkSessionStatus();
          return true;
        }
        // Réponse vide / falsy : on ferme quand même la modal pour
        // ne pas piéger l'utilisateur ; le prochain 401 ( s'il y en a )
        // déclenchera le flow normal de déconnexion.
        this.hideWarningModal();
        return false;
      })
      .catch(() => {
        // Échec /refresh ( réseau , 401 sur token déjà expiré ... ).
        // On NE déclenche PAS handleExpiredSession ici : un click
        // utilisateur sur "Rester connecté" qui logout immédiatement
        // est anti-UX , surtout si la cause est un hoquet réseau. Le
        // prochain appel API renverra 401 et le Fetcher
        // ( notifyCredentialsLost ) gérera la déconnexion proprement.
        this.hideWarningModal();
        return false;
      });
  },
};
